import React from 'react';
import {
  ToolButtonList,
  ToolButton,
  ToolButtonListDefault,
  ToolButtonListDropDown,
  ToolButtonListItem,
  ToolButtonListDivider,
  DropdownMenuSeparator,
} from '@ohif/ui-next';
import { useToolbar } from '@ohif/core/src';

interface ToolButtonListWrapperProps {
  buttonSection: string;
  onInteraction?: (details: { itemId: string; commands?: Record<string, unknown> }) => void;
  id: string;
}

/**
 * Wraps the ToolButtonList component to handle the OHIF toolbar button structure
 * @param props - Component props
 * @returns Component
 * // test
 */
export default function ToolButtonListWrapper({ buttonSection, id }: ToolButtonListWrapperProps) {
  const { onInteraction, toolbarButtons } = useToolbar({
    buttonSection,
  });

  if (!toolbarButtons?.length) {
    return null;
  }

  const primary =
    toolbarButtons.find(button => button.componentProps.isActive)?.componentProps ||
    toolbarButtons[0].componentProps;

  const items = toolbarButtons.map(button => button.componentProps);

  return (
    <ToolButtonList>
      <ToolButtonListDefault>
        <div
          data-cy={`${id}-split-button-primary`}
          data-tool={primary.id}
          data-active={primary.isActive}
        >
          <ToolButton
            {...primary}
            onInteraction={({ itemId }) =>
              onInteraction?.({ id, itemId, commands: primary.commands })
            }
            className={primary.className}
          />
        </div>
      </ToolButtonListDefault>
      <ToolButtonListDivider className={primary.isActive ? 'opacity-0' : 'opacity-100'} />
      <div data-cy={`${id}-split-button-secondary`}>
        <ToolButtonListDropDown>
          {items.map(item => {
            // GLEAMER FORK PATCH: a `divider` entry renders a horizontal separator
            // (e.g. between the window-level presets and Invert), matching the prod
            // ohif.splitButton the toolButtonList migration replaced.
            if (item.type === 'divider') {
              // DropdownMenuSeparator's default `bg-muted` is near-black in the viewer
              // theme and invisible on the dark dropdown; override with the (theme-aware)
              // muted-foreground so it reads as a light divider line.
              return (
                <DropdownMenuSeparator
                  key={item.id}
                  className="bg-muted-foreground"
                />
              );
            }
            // GLEAMER FORK PATCH: render the optional `secondaryLabel` (muted, e.g. a
            // window-level "400 / 40") and a right-aligned `indexLabel` (e.g. the preset
            // number) so split buttons match the prod look the 3.11 ohif.splitButton ->
            // ohif.toolButtonList migration dropped. Destructured out of the spread so
            // they are not forwarded to the DOM; items without them render as before.
            const { secondaryLabel, indexLabel, ...itemProps } = item;
            return (
              <ToolButtonListItem
                key={item.id}
                {...itemProps}
                data-cy={item.id}
                data-tool={item.id}
                data-active={item.isActive}
                onSelect={() => onInteraction?.({ id, itemId: item.id, commands: item.commands })}
              >
                <span className="flex w-full items-center justify-between pl-1">
                  <span className="flex items-baseline gap-2">
                    <span>{item.label || item.tooltip || item.id}</span>
                    {secondaryLabel && (
                      <span className="text-muted-foreground">{secondaryLabel}</span>
                    )}
                  </span>
                  {indexLabel != null && (
                    <span className="text-primary pl-6">{indexLabel}</span>
                  )}
                </span>
              </ToolButtonListItem>
            );
          })}
        </ToolButtonListDropDown>
      </div>
    </ToolButtonList>
  );
}
